/**
 * @file faqSection.component.tsx
 * @path apps/website/src/features/contact/components/faqSection.component.tsx
 * @description Pure presentational FAQ component.
 */

import { FaqItem } from "@sirbastians/types";
import { cn } from "@sirbastians/utils";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { PlusMinusToggle } from "./plusMinusToggle.component";

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

interface FaqSectionProps {
  facade: {
    items: FaqItem[];
    activeIndex: number | null;
    actions: {
      toggle: (index: number) => void;
    };
  };
  className?: string;
}

export const FaqSection = ({ facade, className }: FaqSectionProps) => {
  const { items, activeIndex, actions } = facade;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section
      className={cn("relative py-16 lg:py-24 overflow-hidden", className)}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-3/4 bg-accent/5 blur-[120px] pointer-events-none rounded-full" />

      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        className="container mx-auto max-w-4xl px-6 lg:px-8 relative z-10"
      >
        <div className="text-center mb-16">
          <span className="font-montserrat text-[10px] uppercase tracking-[0.3em] text-accent bg-accent/10 px-3 py-1.5 rounded-sm border border-accent/20">
            Support
          </span>
          <h2 className="mt-8 text-4xl font-montserrat font-bold tracking-tight text-foreground sm:text-5xl">
            Frequently Asked Questions
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-lg font-open-sans text-muted-foreground font-light leading-relaxed">
            Find answers to common questions about our services, booking
            process, and policies. If you don't find what you're looking for,
            feel free to reach out.
          </p>
        </div>

        <motion.div variants={containerVariants} className="space-y-4">
          {items.map((faq, index) => {
            const isActive = index === activeIndex;

            return (
              <motion.div
                key={faq.id}
                variants={itemVariants}
                onClick={() => actions.toggle(index)}
                className={cn(
                  "cursor-pointer rounded-2xl border transition-all duration-300 overflow-hidden",
                  isActive
                    ? "bg-card border-accent/30 scale-[1.02] z-10 relative"
                    : "bg-secondary/30 border-border hover:bg-accent/5 hover:border-accent/20",
                )}
              >
                <div className="flex items-center justify-between p-6 md:p-8 gap-4">
                  <h3
                    className={cn(
                      "font-montserrat font-semibold text-base md:text-lg transition-colors",
                      isActive ? "text-accent" : "text-foreground",
                    )}
                  >
                    {faq.question}
                  </h3>
                  <PlusMinusToggle isOpen={isActive} />
                </div>

                <motion.div
                  initial={false}
                  animate={{
                    height: isActive ? "auto" : 0,
                    opacity: isActive ? 1 : 0,
                  }}
                  transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                >
                  <div className="px-6 pb-6 md:px-8 md:pb-8 pt-0">
                    <div className="h-px w-full bg-border/50 mb-6" />
                    <p className="font-open-sans text-sm md:text-base text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
};
